import { makeStyles, tokens, mergeClasses } from '@fluentui/react-components';
import { brandColors } from '../../theme/brandColors';

/* Single source of truth for Azure DevOps work-item STATE → pill colour.
   Use this everywhere a work-item state is shown so the same status always
   reads the same colour across the app (epics, stories, tasks, trees).

   Colour scheme (per product decision) — SOLID backgrounds, white text:
     Active  → green
     New / not-started     → light blue
     Resolved / awaiting    → purple
     Closed / done         → blue
     Blocked / on-hold     → red
     Removed / cut         → grey

   Implemented as a token-styled span rather than Fluent <Badge color=…> because
   the Badge palette has no purple. All colours come from Fluent palette tokens
   (no hardcoded hex) so dark/high-contrast themes still resolve correctly.

   Scope: work-item *state* only. RAG, bug severity, spec score, customer-roadmap
   status, and invitation status are separate scales with their own colour logic. */

type Hue = 'green' | 'lightBlue' | 'purple' | 'blue' | 'red' | 'grey';

const EXACT: Record<string, Hue> = {
  // green — active work
  active: 'green',
  'in progress': 'green',
  'in-progress': 'green',
  committed: 'green',
  doing: 'green',
  started: 'green',
  // light blue — not started
  new: 'lightBlue',
  proposed: 'lightBlue',
  approved: 'lightBlue',
  open: 'lightBlue',
  'to do': 'lightBlue',
  'to-do': 'lightBlue',
  todo: 'lightBlue',
  backlog: 'lightBlue',
  // purple — dev-complete, awaiting acceptance/sign-off
  resolved: 'purple',
  // blue — done
  closed: 'blue',
  done: 'blue',
  completed: 'blue',
  // grey — removed/cut
  removed: 'grey',
  cut: 'grey',
  abandoned: 'grey',
  // red — blocked
  blocked: 'red',
  'on hold': 'red',
};

export function statusHue(state: string | null | undefined): Hue {
  const s = (state ?? '').trim().toLowerCase();
  if (!s) return 'grey';
  if (EXACT[s]) return EXACT[s];
  // Keyword fallbacks for custom process states.
  if (s.includes('uat') || s.includes('sign-off') || s.includes('sign off') || s.includes('signoff')
    || s.includes('resolve') || s.includes('review') || s.includes('await')) return 'purple';
  if (s.includes('block') || s.includes('hold')) return 'red';
  if (s.includes('progress') || s.includes('active') || s.includes('develop')) return 'green';
  if (s.includes('closed') || s.includes('done') || s.includes('complete')) return 'blue';
  if (s.includes('remov') || s.includes('cancel') || s.includes('abandon')) return 'grey';
  return 'lightBlue'; // unknown → treat as not-yet-started
}

const usePillStyles = makeStyles({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: tokens.borderRadiusCircular,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase200,
    whiteSpace: 'nowrap',
  },
  small: {
    padding: `0 ${tokens.spacingHorizontalS}`,
    fontSize: tokens.fontSizeBase100,
  },
  // Solid Transformr brand colours. White text on the dark fills; near-black on
  // the light green/grey for contrast. Brand hex lives in theme/brandColors.ts —
  // the sanctioned exception to "tokens only".
  green: { backgroundColor: brandColors.green, color: brandColors.white },
  // White text per design preference (note: ~2.5:1 contrast, below WCAG AA).
  lightBlue: { backgroundColor: brandColors.lightBlue, color: brandColors.white },
  purple: { backgroundColor: brandColors.purple, color: brandColors.white },
  blue: { backgroundColor: brandColors.blue, color: brandColors.white },
  red: { backgroundColor: brandColors.red, color: brandColors.white },
  grey: { backgroundColor: brandColors.grey, color: brandColors.white },
});

export interface StatusPillProps {
  /** The raw work-item state string (e.g. "Active", "Awaiting UAT"). */
  state: string | null | undefined;
  size?: 'small' | 'medium';
}

/** Colour-coded SOLID pill for an Azure DevOps work-item state. */
export function StatusPill({ state, size = 'medium' }: StatusPillProps) {
  const styles = usePillStyles();
  const hue = statusHue(state);
  const label = (state ?? '').trim() || 'Unknown';
  return (
    <span className={mergeClasses(styles.base, size === 'small' && styles.small, styles[hue])}>
      {label}
    </span>
  );
}
